import i18n, { useI18N, I18NProps } from "@customer-tools-web/component-i18n";
import { Modal } from "@customer-tools-web/component-ui";
import { FormComponentProps } from "antd/lib/form";
import { WrappedFormUtils } from "antd/lib/form/Form";
import { isEqual } from "lodash";
import * as React from "react";
import { useHistory } from "react-router";

import { useLatestState } from "../hooks/custom-hooks";
import { deepOmitBy, isInvalid } from "../tools";

export type UnsavedChangesProps = {
  setInitialFormValue: (initialFormValue: object) => void;
};

export function useUnsavedChangesHandler(form: WrappedFormUtils) {
  const [initialFormValue, setInitialFormValue] = React.useState({});
  const { t } = useI18N();
  const history = useHistory<{ shouldNotHandleUnsavedChanges: boolean }>();
  const latestState = useLatestState(initialFormValue);
  React.useEffect(() => {
    return history.block((location) => {
      if (location.state && location.state.shouldNotHandleUnsavedChanges) {
        return;
      }
      const isFormValueChanged = !isEqual(
        latestState.current,
        deepOmitBy(form.getFieldsValue(), isInvalid)
      );
      if (isFormValueChanged) {
        return t("unsavedChanges.message");
      }
    });
  }, []);

  return function init(values: object) {
    const validValue = deepOmitBy(values, isInvalid);
    if (!isEqual(validValue, initialFormValue)) {
      setInitialFormValue(validValue);
    }
  };
}

export const withUnsavedChangesHandler = <
  P extends UnsavedChangesProps & FormComponentProps
>(
  Component: React.ComponentType<P>
) => {
  return function UnsavedChangesWrapper(
    props: Omit<P, keyof UnsavedChangesProps>
  ) {
    const setInitialFormValue = useUnsavedChangesHandler(props.form);
    return (
      <Component {...(props as P)} setInitialFormValue={setInitialFormValue} />
    );
  };
};

export default withUnsavedChangesHandler;

class ErrorModal extends React.Component<
  {
    callback: (ok: boolean) => void;
    message: string;
    visible: boolean;
  } & I18NProps,
  { visible: boolean }
> {
  state = {
    visible: this.props.visible,
  };

  render() {
    const { callback, message, t } = this.props;
    return (
      <Modal
        destroyOnClose
        wrapClassName="unsavedChanges"
        content={message}
        title={t("unsavedChanges.title")}
        visible={this.state.visible}
        cancelText={t("unsavedChanges.button.cancel")}
        okText={t("unsavedChanges.button.ok")}
        onCancel={() => {
          this.setState({ visible: false }, () => callback(false));
        }}
        onOk={() => {
          this.setState({ visible: false }, () => callback(true));
        }}
        isFullscreen={false}
      />
    );
  }
}

export const UnSavedChangesErrorModal = i18n(ErrorModal);
